import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select } from '@artosera/ui';
import {
  useComptesBancaires,
  useLignesBancaires,
  useSuggestionsBancaires,
  useRapprocherLigne,
  useClasserLigne,
  useDefaireRapprochement,
  useRapprochementAutomatique,
  useExporterTresorerie,
  messageTresorerie,
  type LigneBancaire,
  type Piece,
  type NatureEcart,
  type NatureClassement,
  type FiltresLignes,
} from '../../api/tresorerie.js';
import { enregistrerFichier } from '../../api/client.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { TresorerieCadre, Montant, DateJour, EtatRequete } from './commun.js';
import { DepenseFormulaire } from './DepenseFormulaire.js';
import styles from './Tresorerie.module.css';
function Panneau({ ligne, fermer }: { ligne: LigneBancaire; fermer: () => void }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const [recherche, setRecherche] = useState('');
  const suggestions = useSuggestionsBancaires(
    galerieSlug,
    ligne.rapprochementId ? '' : ligne.id,
    recherche,
  );
  const rapprocher = useRapprocherLigne(galerieSlug);
  const classer = useClasserLigne(galerieSlug);
  const defaire = useDefaireRapprochement(galerieSlug);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [ecart, setEcart] = useState<NatureEcart | ''>('');
  const [motif, setMotif] = useState('');
  const [nature, setNature] = useState<NatureClassement>('autre');
  const [motifClassement, setMotifClassement] = useState('');
  const [erreur, setErreur] = useState('');
  const [depense, setDepense] = useState(false);
  const label = (c: string) => t(`tresorerie.${c}`);
  async function geste(action: () => Promise<unknown>) {
    setErreur('');
    try {
      await action();
      fermer();
    } catch (e) {
      setErreur(messageTresorerie(e));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) fermer();
      }}
      title={ligne.libelle}
      closeLabel={label('fermer')}
    >
      <p>
        <DateJour date={ligne.dateOperation} /> · <Montant cents={ligne.montantCentimes} /> ·{' '}
        {ligne.reference}
      </p>
      {ligne.rapprochementId ? (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void geste(() => defaire.mutateAsync({ id: ligne.rapprochementId!, motif }));
          }}
        >
          <Field htmlFor="defaire-motif" label={label('motif')}>
            <Input
              id="defaire-motif"
              required
              maxLength={500}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={!motif.trim()} loading={defaire.isPending}>
            {label('defaire')}
          </Button>
        </form>
      ) : (
        <>
          <Field htmlFor="piece-recherche" label={label('recherchePiece')}>
            <Input
              id="piece-recherche"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </Field>
          <EtatRequete requete={suggestions} />
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              void geste(() =>
                rapprocher.mutateAsync({
                  ligneId: ligne.id,
                  pieces: pieces.map(({ type, id }) => ({ type, id })),
                  ...(ecart ? { ecart: { nature: ecart, motif } } : {}),
                }),
              );
            }}
          >
            <fieldset>
              <legend>{label('suggestions')}</legend>
              {(suggestions.data?.suggestions ?? []).map((p) => (
                <label key={`${p.type}:${p.id}`} className={styles.actions}>
                  <input
                    type="checkbox"
                    checked={pieces.some((x) => x.type === p.type && x.id === p.id)}
                    onChange={(e) =>
                      setPieces(
                        e.target.checked
                          ? [...pieces, p]
                          : pieces.filter((x) => x.type !== p.type || x.id !== p.id),
                      )
                    }
                  />
                  {label(`typesPieces.${p.type}`)} · {p.numero} · {p.contactNom} ·{' '}
                  <DateJour date={p.date} /> · <Montant cents={p.montantCentimes} />
                </label>
              ))}
            </fieldset>
            <p>{label('ecartAide')}</p>
            <Field htmlFor="ecart-nature" label={label('natureEcart')}>
              <select
                id="ecart-nature"
                value={ecart}
                onChange={(e) => setEcart(e.target.value as NatureEcart | '')}
              >
                <option value="">{label('sansEcart')}</option>
                {['frais_bancaires', 'commission_paiement', 'arrondi', 'autre'].map((v) => (
                  <option key={v} value={v}>
                    {label(`naturesEcart.${v}`)}
                  </option>
                ))}
              </select>
            </Field>
            {ecart && (
              <Field htmlFor="ecart-motif" label={label('motifEcart')}>
                <Input
                  id="ecart-motif"
                  required
                  maxLength={500}
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                />
              </Field>
            )}
            <Button
              type="submit"
              loading={rapprocher.isPending}
              disabled={pieces.length === 0 || (!!ecart && !motif.trim())}
            >
              {label('confirmerRapprochement')}
            </Button>
          </form>
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              void geste(() =>
                classer.mutateAsync({ id: ligne.id, nature, motif: motifClassement }),
              );
            }}
          >
            <h2>{label('classer')}</h2>
            <Field htmlFor="classement-nature" label={label('natureClassement')}>
              <Select
                id="classement-nature"
                value={nature}
                onValueChange={(v) => setNature(v as NatureClassement)}
                options={['virement_interne', 'apport', 'emprunt', 'impot', 'salaire', 'autre'].map(
                  (v) => ({ value: v, label: label(`naturesClassement.${v}`) }),
                )}
              />
            </Field>
            <Field htmlFor="classement-motif" label={label('motif')}>
              <Input
                id="classement-motif"
                required
                maxLength={500}
                value={motifClassement}
                onChange={(e) => setMotifClassement(e.target.value)}
              />
            </Field>
            <Button type="submit" loading={classer.isPending} disabled={!motifClassement.trim()}>
              {label('classer')}
            </Button>
          </form>
          {ligne.montantCentimes < 0 && (
            <>
              <Button type="button" onClick={() => setDepense(!depense)}>
                {label('depenseDepuisLigne')}
              </Button>
              {depense && <DepenseFormulaire ligne={ligne} onClose={fermer} />}
            </>
          )}
        </>
      )}
      {erreur && <p role="alert">{erreur}</p>}
    </Dialog>
  );
}
function Contenu() {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const [filtres, setFiltres] = useState<FiltresLignes>({ page: 1 });
  const q = useLignesBancaires(galerieSlug, filtres);
  const comptes = useComptesBancaires(galerieSlug);
  const automatique = useRapprochementAutomatique(galerieSlug);
  const exporter = useExporterTresorerie(galerieSlug);
  const [ligne, setLigne] = useState<LigneBancaire>();
  const [erreur, setErreur] = useState('');
  const [demande, setDemande] = useState(false);
  const peutEcrire = galerieALaction(galerie, 'treasury:write');
  const label = (c: string) => t(`tresorerie.${c}`);
  return (
    <>
      <div className={styles.actions}>
        <Field htmlFor="lignes-compte" label={label('compte')}>
          <select
            id="lignes-compte"
            value={filtres.compteId ?? ''}
            onChange={(e) =>
              setFiltres({ ...filtres, compteId: e.target.value || undefined, page: 1 })
            }
          >
            <option value="">{label('tous')}</option>
            {comptes.data?.comptes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.libelle}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="lignes-etat" label={label('etat')}>
          <select
            id="lignes-etat"
            value={filtres.etat ?? ''}
            onChange={(e) =>
              setFiltres({
                ...filtres,
                etat: (e.target.value || undefined) as FiltresLignes['etat'],
                page: 1,
              })
            }
          >
            {['', 'a_traiter', 'rapprochee'].map((v) => (
              <option key={v} value={v}>
                {label(v || 'tous')}
              </option>
            ))}
          </select>
        </Field>
        {(['du', 'au'] as const).map((c) => (
          <Field key={c} htmlFor={`lignes-${c}`} label={label(c)}>
            <Input
              id={`lignes-${c}`}
              type="date"
              value={filtres[c] ?? ''}
              onChange={(e) =>
                setFiltres({ ...filtres, [c]: e.target.value || undefined, page: 1 })
              }
            />
          </Field>
        ))}
        <Button
          disabled={!filtres.du || !filtres.au || filtres.du > filtres.au}
          loading={exporter.isPending}
          onClick={() => {
            void exporter
              .mutateAsync({ du: filtres.du!, au: filtres.au! })
              .then((f) => enregistrerFichier(f, 'tresorerie.csv'))
              .catch((e: unknown) => setErreur(messageTresorerie(e)));
          }}
        >
          {label('exporter')}
        </Button>
        {peutEcrire && (
          <Button
            loading={automatique.isPending}
            onClick={() => {
              void automatique
                .mutateAsync({ compteId: filtres.compteId })
                .then(() => setDemande(true))
                .catch((e: unknown) => setErreur(messageTresorerie(e)));
            }}
          >
            {label('rapprocherAutomatiquement')}
          </Button>
        )}
      </div>
      {demande && <p role="status">{label('rapprochementEnAttente')}</p>}
      {erreur && <p role="alert">{erreur}</p>}
      <EtatRequete requete={comptes} />
      <EtatRequete requete={q} />
      {q.data && (
        <>
          <div className={styles.tableau}>
            <table>
              <caption>{label('lignes')}</caption>
              <thead>
                <tr>
                  {['compte', 'date', 'dateValeur', 'libelle', 'reference', 'montant', 'etat'].map(
                    (c) => (
                      <th scope="col" key={c}>
                        {label(c)}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {q.data.lignes.map((l) => (
                  <tr key={l.id}>
                    <td>{comptes.data?.comptes.find((c) => c.id === l.compteId)?.libelle}</td>
                    <td>
                      <DateJour date={l.dateOperation} />
                    </td>
                    <td>
                      <DateJour date={l.dateValeur} />
                    </td>
                    <td>{l.libelle}</td>
                    <td>{l.reference}</td>
                    <td>
                      <Montant cents={l.montantCentimes} />
                    </td>
                    <td>
                      {label(l.rapprochementId ? 'rapprochee' : 'a_traiter')}{' '}
                      {peutEcrire && (
                        <Button onClick={() => setLigne(l)}>
                          {label(l.rapprochementId ? 'defaire' : 'rapprocher')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.actions}>
            <Button
              disabled={q.data.pagination.page <= 1}
              onClick={() => setFiltres({ ...filtres, page: q.data.pagination.page - 1 })}
            >
              {label('precedent')}
            </Button>
            <span>
              {q.data.pagination.page} / {q.data.pagination.pages}
            </span>
            <Button
              disabled={q.data.pagination.page >= q.data.pagination.pages}
              onClick={() => setFiltres({ ...filtres, page: q.data.pagination.page + 1 })}
            >
              {label('suivant')}
            </Button>
          </div>
        </>
      )}
      {ligne && peutEcrire && (
        <Panneau key={ligne.id} ligne={ligne} fermer={() => setLigne(undefined)} />
      )}
    </>
  );
}
export function LignesPage() {
  return (
    <TresorerieCadre titre="lignes">
      <Contenu />
    </TresorerieCadre>
  );
}
